import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api';
import { useActiveCompany } from '../components/CompanySwitcher';

/**
 * Which folder the forms list is filtered to, held in the URL.
 *
 * The folder rail moved out of the page and into the app's one sidebar, so the choice is
 * now made by a component that does not render the list. The URL is the only place both
 * can read it from without one owning the other — and it makes a filtered list something
 * you can link to, which page state never was.
 *
 * null is "everything"; 'none' is "not filed yet". They are different questions, and the
 * second cannot be expressed by leaving the parameter out.
 */
export function useFolderParam(): [string | null, (next: string | null) => void] {
  const [params, setParams] = useSearchParams();
  const folder = params.get('folder');

  const setFolder = (next: string | null) => {
    const updated = new URLSearchParams(params);
    if (next) updated.set('folder', next);
    else updated.delete('folder');
    setParams(updated, { replace: true });
  };

  return [folder, setFolder];
}

export interface FormsOverview {
  forms: number;
  live: number;
  draft: number;
  responses: number;
}

/**
 * The counts in the command bar.
 *
 * Deliberately its own query rather than derived from whatever the list currently shows:
 * the list is filtered by folder and by search, and a readout that changed when you typed
 * would be reporting on the filter rather than on the company.
 */
export function useFormsOverview() {
  const { active } = useActiveCompany();

  const query = useQuery({
    queryKey: ['forms', 'overview', active?.id ?? 'all'],
    queryFn: () => api.forms.list(active ? { companyId: active.id } : undefined),
    staleTime: 30_000,
    refetchInterval: 60_000,
  });

  const forms = query.data?.data ?? [];
  const live = forms.filter((f) => f.status === 'Published').length;

  const overview: FormsOverview = {
    forms: forms.length,
    live,
    draft: forms.length - live,
    responses: forms.reduce((total, f) => total + (f.responseCount ?? 0), 0),
  };

  return { overview, query };
}

/**
 * Whether the API is answering, and since when it stopped.
 *
 * The interesting number is the moment it went away, not the moment of the most recent
 * failed retry — "unreachable since 14:02" tells you how long you have been looking at
 * stale rows, where "since 14:09" (the last of six retries) tells you nothing. React
 * Query re-stamps errorUpdatedAt on every attempt, so the first one is captured here.
 */
export function useApiStatus() {
  const { query } = useFormsOverview();
  const queryClient = useQueryClient();
  const [downSince, setDownSince] = useState<Date | null>(null);
  const wasDown = useRef(false);

  const failing = query.isError;

  useEffect(() => {
    if (failing && !wasDown.current) {
      wasDown.current = true;
      setDownSince(new Date());
    } else if (!failing && wasDown.current) {
      wasDown.current = false;
      setDownSince(null);
    }
  }, [failing]);

  const retry = () => {
    // Everything is suspect after an outage, not just the query that noticed it.
    void queryClient.refetchQueries();
  };

  return {
    offline: failing,
    downSince,
    // Rows are still on screen from the last good fetch; say so rather than let them
    // pass as current.
    hasStaleData: failing && query.data !== undefined,
    retrying: query.isFetching,
    retry,
  };
}
